import { ImageLoadState } from '@fluentui/react';
import {
  forwardRef,
  HTMLAttributes,
  KeyboardEvent,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { IComponent, IconName, KeyboardKey } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { Button } from '../Button/Button';
import {
  buttonStylesIcon,
  buttonStylesIconCarousel,
  buttonStylesIconCarouselLeft,
  buttonStylesIconCarouselRight,
} from '../Button/Button.styles';
import { Image } from '../Image/Image';
import { imageStylesCarousel, imageStylesSingle } from '../Image/Image.styles';
import { Pagination } from '../Pagination/Pagination';
import { carouselStylesDefault, gap, ICarouselStyles } from './Carousel.styles';

export enum CarouselItemType {
  image = 'image',
  custom = 'custom',
}

interface ICarouselClassNames {
  root: string;
  leftButtonContainer: string;
  rightButtonContainer: string;
  pagination: string;
  container: string;
  itemsContainer: string;
  items: string;
  item: string;
}

export interface ICarouselProps extends IComponent<ICarouselStyles>, HTMLAttributes<HTMLDivElement> {
  type?: CarouselItemType;
  idField?: string;
  selectedId?: string | number | undefined;
  data?: { [key: string]: any }[];
  multiple?: boolean;
  height?: string | number;
  onRenderItem?: (item: { [key: string]: any }) => ReactNode;
  onClickItem?: (index: number | undefined, id: string | number | undefined) => void;
}

export const Carousel = forwardRef((props: ICarouselProps, ref: Ref<HTMLDivElement>) => {
  const {
    className,
    styles,
    data = [],
    idField = 'id',
    selectedId = undefined,
    multiple = true,
    type = CarouselItemType.image,
    height = '100%',
    onClickItem,
    onRenderItem = () => null,
    ...elementProps
  } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [needScroll, setNeedScroll] = useState(true);
  const refRoot = useRef<HTMLDivElement>(null);
  const refItems = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  const classNames = useClassNames<ICarouselStyles, ICarouselClassNames>(
    'Carousel',
    carouselStylesDefault,
    styles,
    className,
    !multiple && 'single'
  );
  let unloadedImageCount = data.length;
  const lastIndex = data.length - 1;

  // css scroll-behavior doesn't support IE and Safari, so we need to use translateX for the scroll animation.
  const getLeft = (): number => {
    const itemsEl = refItems.current;
    if (itemsEl) {
      const left = +(itemsEl.style.transform.match(/(-?[0-9.]+)/g) || 0);
      return Math.abs(left);
    }
    return 0;
  };

  const scrollById = useCallback(
    (selectedId: string | number | undefined) => {
      if (selectedId !== undefined) {
        const itemsEl = refItems.current;
        if (itemsEl) {
          const children = itemsEl.children;
          let itemsWidth = 0;
          data.some((item, i) => {
            if (item[idField] === selectedId) {
              setSelectedIndex(i);
              return true;
            } else {
              itemsWidth += children[i].clientWidth + gap;
              return false;
            }
          });
          const maxWidth = itemsEl.clientWidth - itemsEl.parentElement!.clientWidth;
          itemsWidth = maxWidth < itemsWidth ? maxWidth : itemsWidth;
          itemsEl.style.transform = `translateX(-${itemsWidth}px)`;
        }
      }
    },
    [data, idField]
  );

  const scrollByIndex = (index: number) => {
    scrollById(data[getValidIndex(index)][idField]);
  };

  const getValidIndex = (index: number) => {
    return index < 0 ? 0 : index > lastIndex ? lastIndex : index;
  };

  const scrollToLeftHiddenItem = () => {
    const itemsEl = refItems.current;
    if (itemsEl) {
      const left = getLeft();
      const children = Array.from(itemsEl.children);
      let itemsWidth = 0;
      children.some((el, i) => {
        const oldItemsWidth = itemsWidth;
        itemsWidth += el.clientWidth + gap;
        if (itemsWidth >= left) {
          itemsWidth = oldItemsWidth;
          setSelectedIndex(i);
          return true;
        } else {
          return false;
        }
      });
      itemsEl.style.transform = `translateX(-${itemsWidth}px)`;
    }
  };

  const scrollToRightHiddenItem = () => {
    const itemsEl = refItems.current;
    if (itemsEl) {
      const itemsContainerEl = itemsEl.parentElement as HTMLElement;
      const left = getLeft();
      const children = Array.from(itemsEl.children);
      // find the first hided right item
      const containerWidth = itemsContainerEl.offsetWidth;
      const right = left + containerWidth;
      let itemsWidth = 0;
      const item = children.find((el, i) => {
        itemsWidth += el.clientWidth;
        if (itemsWidth > right) {
          setSelectedIndex(i);
          return true;
        } else {
          itemsWidth += gap;
          return false;
        }
      });
      if (item) {
        itemsEl.style.transform = `translateX(-${itemsWidth - containerWidth}px)`;
      }
    }
  };

  const checkNeedScroll = useCallback(() => {
    let _needScroll = true;
    if (multiple) {
      const itemsEl = refItems.current;
      if (itemsEl) {
        const itemsContainerEl = itemsEl.parentElement;
        if (itemsContainerEl && itemsContainerEl.clientWidth >= itemsEl.clientWidth) {
          _needScroll = false;
        }
      }
    } else {
      _needScroll = data.length > 1;
    }
    if (_needScroll !== needScroll) {
      setNeedScroll(_needScroll);
    }
  }, [data, multiple, needScroll]);

  const onClickButton = (left = false) => {
    if (left) {
      if (multiple) {
        scrollToLeftHiddenItem();
      } else {
        setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : 0);
      }
    } else {
      if (multiple) {
        scrollToRightHiddenItem();
      } else {
        setSelectedIndex(selectedIndex < lastIndex ? selectedIndex + 1 : lastIndex);
      }
    }
  };

  const onClickPagination = (page: number) => {
    const index = getValidIndex(page - 1);
    if (multiple) {
      scrollByIndex(index);
    } else {
      setSelectedIndex(index);
    }
  };

  const onKeyUp = (e: KeyboardEvent) => {
    switch (e.key) {
      case KeyboardKey.ArrowLeft:
        onClickPagination(selectedIndex);
        break;
      case KeyboardKey.ArrowRight:
        onClickPagination(selectedIndex + 2);
        break;
    }
  };

  const onRenderImage = (item: { [key: string]: any }) => {
    const imageStyles = [imageStylesCarousel];
    if (!multiple) {
      imageStyles.push(imageStylesSingle);
    }
    return (
      <Image
        styles={imageStyles}
        src={item[idField]}
        onLoadingStateChange={(loadState) => {
          if (unloadedImageCount <= 0) {
            return;
          }
          // after all images are loaded, we can check the total size of the items
          if (loadState !== ImageLoadState.notLoaded) {
            // eslint-disable-next-line react-hooks/immutability
            unloadedImageCount = multiple ? unloadedImageCount - 1 : 0;
            if (unloadedImageCount <= 0) {
              checkNeedScroll();
            }
          }
        }}
      />
    );
  };
  const onRenderSingleItem = (item: { [key: string]: any }, index: number) => {
    if (!item) {
      return;
    }
    return (
      <div
        key={item[idField]}
        className={classNames.item}
        data-id={item[idField]}
        data-index={index}
        onClick={(e) => {
          const dataset = e.currentTarget.dataset;
          onClickItem!(dataset.index ? +dataset.index : undefined, dataset.id);
        }}
      >
        {onRenderItem(item)
          ? onRenderItem(item)
          : type === CarouselItemType.image
          ? onRenderImage(item)
          : item[idField]}
      </div>
    );
  };
  const onRenderMultipleItems = () => {
    return data.map((item, i) => onRenderSingleItem(item, i));
  };

  const onRenderItems = () => {
    return multiple ? onRenderMultipleItems() : onRenderSingleItem(data[selectedIndex], selectedIndex);
  };

  useEffect(() => {
    if (selectedId !== undefined) {
      if (multiple) {
        scrollById(selectedId);
      } else {
        const index = data.findIndex((item) => item[idField] === selectedId);
        setSelectedIndex(index);
      }
    }
    if (type !== CarouselItemType.image) {
      checkNeedScroll();
    }
  }, [selectedId, scrollById, multiple, idField, data, checkNeedScroll, type]);

  return (
    <div {...elementProps} className={classNames.root} style={{ height }} ref={refRoot} onKeyUp={onKeyUp}>
      <div className={classNames.container}>
        <div className={classNames.itemsContainer}>
          <div className={classNames.items} ref={refItems}>
            {onRenderItems()}
          </div>
        </div>
        <div className={classNames.leftButtonContainer}>
          <Button
            styles={[buttonStylesIcon, buttonStylesIconCarousel, buttonStylesIconCarouselLeft]}
            iconName={IconName.ChevronLeftSmall}
            disabled={selectedIndex === 0 || !needScroll}
            title="Previous"
            onClick={() => onClickButton(true)}
          />
        </div>
        <div className={classNames.rightButtonContainer}>
          <Button
            styles={[buttonStylesIcon, buttonStylesIconCarousel, buttonStylesIconCarouselRight]}
            iconName={IconName.ChevronRightSmall}
            disabled={selectedIndex === lastIndex || !needScroll}
            title="Next"
            onClick={() => onClickButton()}
          />
        </div>
      </div>
      <div className={classNames.pagination}>
        <Pagination count={data.length} selectedPage={selectedIndex + 1} onClick={onClickPagination}></Pagination>
      </div>
    </div>
  );
});
